import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mail, CheckCircle, XCircle, Search, Send, FileText, X, Clock, History, Paperclip, Save } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Template predefiniti. Il body è editabile prima dell'invio.
const TEMPLATES = [
  {
    id: "doc_mancanti",
    titolo: "Richiesta Documenti Mancanti",
    bg: "#2563eb", bgHover: "#1d4ed8",
    body: `<p>Buongiorno,</p>
<p>per completare la Sua pratica abbiamo bisogno di ricevere copia dei seguenti documenti:</p>
<ul>
  <li>Codice Fiscale (fronte e retro)</li>
  <li>Documento di Identità (fronte e retro)</li>
</ul>
<p>Pu&ograve; rispondere a questa email allegando le scansioni/foto o inviarli via WhatsApp al numero 070 204 3952.</p>
<p>Cordiali saluti,<br/>Showroom Ingenio</p>`,
  },
  {
    id: "appuntamento_confermato",
    titolo: "Appuntamento Confermato",
    bg: "#059669", bgHover: "#047857",
    body: `<p>Buongiorno,</p>
<p>confermiamo l'appuntamento in showroom nella data e orario concordati. La aspettiamo in Via Puccini 71, Cagliari.</p>
<p>In caso di imprevisti pu&ograve; contattarci al numero 070 204 3952.</p>
<p>A presto!<br/>Showroom Ingenio</p>`,
  },
  {
    id: "promemoria_pagamento",
    titolo: "Promemoria Pagamento",
    bg: "#f59e0b", bgHover: "#d97706",
    body: `<p>Buongiorno,</p>
<p>Le ricordiamo che risulta in scadenza il pagamento della prossima tranche prevista dal contratto.</p>
<p>La preghiamo di procedere al bonifico entro la data concordata. Per qualsiasi chiarimento resta a disposizione il nostro ufficio amministrativo.</p>
<p>Cordiali saluti,<br/>Showroom Ingenio</p>`,
  },
  {
    id: "recensione_google",
    titolo: "Richiesta Recensione Google",
    bg: "#9333ea", bgHover: "#7e22ce",
    body: `<p>Buongiorno,</p>
<p>la Sua opinione &egrave; molto importante per noi. Se si &egrave; trovato bene con il nostro servizio, Le chiediamo cortesemente di lasciare una recensione su Google:</p>
<p><a href="https://g.page/r/showroom-ingenio/review">👉 Lascia la tua recensione</a></p>
<p>Grazie di cuore!<br/>Showroom Ingenio</p>`,
  },
  {
    id: "followup_preventivo",
    titolo: "Follow-up Preventivo",
    bg: "#f97316", bgHover: "#ea580c",
    body: `<p>Buongiorno,</p>
<p>torniamo a Lei per sapere se ha avuto modo di valutare il preventivo che Le abbiamo inviato.</p>
<p>Resto a disposizione per qualsiasi chiarimento o modifica. Pu&ograve; rispondere a questa email o chiamarci al numero 070 204 3952.</p>
<p>Cordiali saluti,<br/>Showroom Ingenio</p>`,
  },
  {
    id: "sollecito_firma",
    titolo: "Sollecito Firma Contratto",
    bg: "#dc2626", bgHover: "#b91c1c",
    body: `<p>Buongiorno,</p>
<p>Le ricordiamo che il contratto risulta ancora da restituire firmato. Senza la firma non possiamo procedere con l'evasione dell'ordine.</p>
<p>Pu&ograve; firmare i documenti e inviarli via mail a <a href="mailto:amministrazione@showroomingenio.it">amministrazione@showroomingenio.it</a> oppure caricarli sul Portale Cliente.</p>
<p>Cordiali saluti,<br/>Showroom Ingenio</p>`,
  },
  {
    id: "ringraziamento",
    titolo: "Ringraziamento Cliente",
    bg: "#1e293b", bgHover: "#0f172a",
    body: `<p>Gentile Cliente,</p>
<p>desideriamo ringraziarLa per la fiducia che ha riposto in noi scegliendo Showroom Ingenio. &Egrave; stato un piacere lavorare insieme.</p>
<p>Per qualsiasi necessit&agrave; futura siamo a Sua completa disposizione.</p>
<p>Un caro saluto,<br/>Showroom Ingenio</p>`,
  },
  {
    id: "comunicazione_generica",
    titolo: "Comunicazione Generica",
    bg: "#78350f", bgHover: "#451a03",
    body: `<p>Buongiorno,</p>
<p>[Inserire qui il testo della comunicazione]</p>
<p>Cordiali saluti,<br/>Showroom Ingenio</p>`,
  },
];

export default function TemplateEmailPage({ user, logout }) {
  const [gmailStatus, setGmailStatus] = useState({ loading: true, connected: false, hasRefresh: false });
  const [openDialog, setOpenDialog] = useState(false);
  const [currentTpl, setCurrentTpl] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templates, setTemplates] = useState(TEMPLATES);
  const [savingTpl, setSavingTpl] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [recipient, setRecipient] = useState(null);
  const [ccEmail, setCcEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState("");
  const fileInputRef = useRef(null);
  const searchDebounceRef = useRef(null);

  const fetchGmailStatus = async () => {
    setGmailStatus((p) => ({ ...p, loading: true }));
    try {
      const res = await axios.get(`${API}/oauth/gmail/status`, { withCredentials: true });
      setGmailStatus({
        loading: false,
        connected: !!res.data?.connected,
        hasRefresh: !!res.data?.has_refresh_token,
      });
    } catch (e) {
      setGmailStatus({ loading: false, connected: false, hasRefresh: false });
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/email-drafts/history`, {
        params: { limit: 20 },
        withCredentials: true,
      });
      setHistory(res.data?.items || []);
    } catch (e) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/email-templates`, { withCredentials: true });
      const stored = res.data?.items || [];
      if (stored.length > 0) {
        const byId = Object.fromEntries(stored.map((t) => [t.id, t]));
        setTemplates(TEMPLATES.map((d) => (byId[d.id] ? { ...d, ...byId[d.id] } : d)));
      }
    } catch (_) { /* fallback locali */ }
  };

  useEffect(() => {
    fetchGmailStatus();
    fetchHistory();
    fetchTemplates();
  }, []);

  const connectGmail = async () => {
    try {
      const r = await axios.get(`${API}/oauth/gmail/login?return_to=/template-email`, { withCredentials: true });
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore collegamento Google");
    }
  };

  const openTemplateDialog = (tpl) => {
    setCurrentTpl(tpl);
    setSubject(tpl.subject || tpl.titolo);
    setBody(tpl.body);
    setQuery("");
    setResults([]);
    setRecipient(null);
    setCcEmail("");
    setAttachments([]);
    setAttachmentError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    setOpenDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!currentTpl) return;
    setSavingTpl(true);
    try {
      await axios.put(
        `${API}/email-templates/${currentTpl.id}`,
        { titolo: currentTpl.titolo, subject: subject.trim(), body },
        { withCredentials: true }
      );
      // Aggiorna lo state locale
      setTemplates((prev) =>
        prev.map((t) =>
          t.id === currentTpl.id ? { ...t, subject: subject.trim(), body } : t
        )
      );
      setCurrentTpl({ ...currentTpl, subject: subject.trim(), body });
      toast.success("Template salvato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingTpl(false);
    }
  };

  const MAX_ATT_MB = 20;
  const MAX_TOTAL_MB = 25;

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result || "";
        const base64 = String(result).split(",")[1] || "";
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFilesSelected = async (fileList) => {
    setAttachmentError("");
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const currentSize = attachments.reduce((s, a) => s + a.size, 0);
    const newItems = [];
    let runningSize = currentSize;
    for (const f of files) {
      if (f.size > MAX_ATT_MB * 1024 * 1024) {
        setAttachmentError(`"${f.name}" supera i ${MAX_ATT_MB} MB.`);
        continue;
      }
      if (runningSize + f.size > MAX_TOTAL_MB * 1024 * 1024) {
        setAttachmentError(`Dimensione totale allegati oltre ${MAX_TOTAL_MB} MB. "${f.name}" scartato.`);
        continue;
      }
      try {
        const b64 = await readFileAsBase64(f);
        newItems.push({ filename: f.name, mime: f.type || "application/octet-stream", size: f.size, base64: b64 });
        runningSize += f.size;
      } catch (e) {
        setAttachmentError(`Impossibile leggere il file "${f.name}".`);
      }
    }
    if (newItems.length) setAttachments((prev) => [...prev, ...newItems]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const formatBytes = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  const handleQueryChange = (val) => {
    setQuery(val);
    setRecipient(null);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if ((val || "").trim().length < 2) {
      setResults([]);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/contacts/search`, {
          params: { q: val.trim(), limit: 25 },
          withCredentials: true,
        });
        setResults(res.data?.results || []);
      } catch (e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const selectRecipient = (r) => {
    setRecipient(r);
    setQuery(`${r.cognome} ${r.nome}`.trim());
    setResults([]);
  };

  const clearRecipient = () => {
    setRecipient(null);
    setQuery("");
    setResults([]);
  };

  const handleCreateDraft = async () => {
    if (!recipient || !recipient.email) {
      toast.error("Seleziona un destinatario con email valida");
      return;
    }
    if (!subject.trim()) {
      toast.error("L'oggetto non pu\u00f2 essere vuoto");
      return;
    }
    setCreating(true);
    try {
      await axios.post(
        `${API}/gmail/create-draft`,
        {
          to_email: recipient.email,
          subject: subject.trim(),
          body_html: body,
          cc_email: ccEmail.trim() || null,
          template_id: currentTpl?.id || null,
          template_titolo: currentTpl?.titolo || null,
          recipient_nome: recipient.nome || "",
          recipient_cognome: recipient.cognome || "",
          attachments: attachments.map(a => ({ base64: a.base64, filename: a.filename, mime: a.mime })),
        },
        { withCredentials: true }
      );
      toast.success("Bozza pronta! Trovi la mail nelle bozze di Gmail \u2709\ufe0f", { duration: 5000 });
      setOpenDialog(false);
      fetchHistory();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore durante la creazione della bozza";
      toast.error(detail);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="lg:ml-64 p-4 lg:p-8 transition-all">
        <div className="max-w-5xl mx-auto space-y-6">
          <div className="flex items-center gap-3">
            <Mail className="w-7 h-7 text-blue-600" />
            <h1 className="text-2xl font-bold text-slate-900">Template Email</h1>
          </div>

          {/* Status Gmail */}
          <Card
            className={`border-2 ${
              gmailStatus.loading
                ? "border-slate-200 bg-slate-50"
                : gmailStatus.connected
                  ? "border-emerald-300 bg-emerald-50"
                  : "border-red-300 bg-red-50"
            }`}
            data-testid="gmail-status-card"
          >
            <CardContent className="py-4">
              {gmailStatus.loading ? (
                <p className="text-sm text-slate-600">Verifica connessione Gmail in corso...</p>
              ) : gmailStatus.connected ? (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-2 text-emerald-800">
                    <CheckCircle className="w-5 h-5" />
                    <div>
                      <p className="font-semibold">Gmail connesso</p>
                      <p className="text-xs">Le bozze verranno create nel tuo account Gmail collegato.</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={connectGmail}
                    data-testid="gmail-reconnect-btn"
                  >
                    Riconnetti
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-2 text-red-800">
                    <XCircle className="w-5 h-5" />
                    <div>
                      <p className="font-semibold">Gmail non connesso</p>
                      <p className="text-xs">Devi autorizzare Gmail per poter creare bozze dai template.</p>
                    </div>
                  </div>
                  <Button
                    onClick={connectGmail}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="gmail-connect-btn"
                  >
                    Collega Gmail
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Grid Template */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5 text-slate-600" />
                Scegli un template
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => openTemplateDialog(tpl)}
                    disabled={!gmailStatus.connected}
                    data-testid={`template-btn-${tpl.id}`}
                    data-state="colored"
                    style={{ backgroundColor: tpl.bg }}
                    onMouseEnter={(e) => { if (gmailStatus.connected) e.currentTarget.style.backgroundColor = tpl.bgHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = tpl.bg; }}
                    className={`text-white font-semibold rounded-xl px-4 py-6 text-left shadow-md transition-transform ${
                      gmailStatus.connected ? "hover:scale-[1.02] hover:shadow-lg" : "opacity-60 cursor-not-allowed"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <Mail className="w-5 h-5 shrink-0 mt-0.5 opacity-90" />
                      <span className="text-base leading-tight">{tpl.titolo}</span>
                    </div>
                  </button>
                ))}
              </div>
              {!gmailStatus.connected && !gmailStatus.loading && (
                <p className="text-xs text-slate-500 italic mt-4">
                  Collega Gmail per attivare i template.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Cronologia Bozze */}
          <Card data-testid="draft-history-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <History className="w-5 h-5 text-slate-600" />
                Cronologia Bozze
                {!historyLoading && history.length > 0 && (
                  <span className="text-xs font-normal text-slate-500">({history.length})</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <p className="text-sm text-slate-500 italic">Caricamento...</p>
              ) : history.length === 0 ? (
                <p className="text-sm text-slate-500 italic">Nessuna bozza creata finora. Clicca un template qui sopra per iniziare.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {history.map((h) => {
                    const dt = h.created_at ? new Date(h.created_at) : null;
                    const dtStr = dt ? dt.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                    const destLabel = `${h.recipient_cognome || ''} ${h.recipient_nome || ''}`.trim() || h.to_email;
                    return (
                      <div
                        key={h.id}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3"
                        data-testid={`draft-history-item-${h.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                              <Mail className="w-3 h-3" />
                              {h.template_titolo || h.subject}
                            </span>
                            <span className="font-medium text-sm text-slate-900 truncate">
                              {destLabel}
                            </span>
                            {Array.isArray(h.attachments_filenames) && h.attachments_filenames.length > 0 && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"
                                title={h.attachments_filenames.join(", ")}
                              >
                                <Paperclip className="w-3 h-3" />
                                {h.attachments_filenames.length} allegat{h.attachments_filenames.length === 1 ? "o" : "i"}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-600 mt-0.5 truncate">
                            <span className="text-slate-500">A:</span> {h.to_email}
                            {h.cc_email && <> · <span className="text-slate-500">CC:</span> {h.cc_email}</>}
                            {h.created_by_email && <> · <span className="text-slate-500">da</span> {h.created_by_email}</>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-slate-500 shrink-0">
                          <Clock className="w-3 h-3" />
                          {dtStr}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialog Composizione */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-email-dialog">
          <DialogHeader>
            <DialogTitle>Nuova Bozza · {currentTpl?.titolo || ""}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Destinatario */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Destinatario *</Label>
              {recipient ? (
                <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900">
                      {recipient.cognome} {recipient.nome}
                    </p>
                    <p className="text-xs text-slate-600 truncate">
                      {recipient.email || <span className="text-red-600 italic">Nessuna email</span>}
                      {recipient.telefono && <> · {recipient.telefono}</>}
                      <span className="ml-2 text-slate-400">({recipient.source})</span>
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearRecipient}
                    data-testid="clear-recipient-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <Input
                      value={query}
                      onChange={(e) => handleQueryChange(e.target.value)}
                      placeholder="Cerca per cognome, nome, telefono o email (min. 2 caratteri)"
                      className="pl-9"
                      data-testid="recipient-search-input"
                      autoFocus
                    />
                  </div>
                  {query.trim().length >= 2 && (
                    <div className="mt-1 border border-slate-200 rounded-lg bg-white shadow-sm max-h-60 overflow-y-auto">
                      {searching && (
                        <p className="text-xs text-slate-500 italic p-3">Ricerca in corso...</p>
                      )}
                      {!searching && results.length === 0 && (
                        <p className="text-xs text-slate-500 italic p-3">Nessun contatto trovato</p>
                      )}
                      {!searching && results.map((r) => (
                        <button
                          key={`${r.source}-${r.id}`}
                          type="button"
                          onClick={() => selectRecipient(r)}
                          className="block w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 last:border-0"
                          data-testid={`recipient-result-${r.id}`}
                        >
                          <div className="font-medium text-sm text-slate-900">
                            {r.cognome} {r.nome}
                          </div>
                          <div className="text-xs text-slate-600 truncate">
                            {r.email || <span className="text-red-600">Nessuna email</span>}
                            {r.telefono && <> · {r.telefono}</>}
                            <span className="ml-2 text-slate-400">({r.source})</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* CC */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">
                Copia Conoscenza (CC) <span className="text-slate-400 font-normal">— opzionale</span>
              </Label>
              <Input
                type="email"
                value={ccEmail}
                onChange={(e) => setCcEmail(e.target.value)}
                placeholder="esempio@dominio.it"
                data-testid="cc-email-input"
              />
            </div>

            {/* Oggetto */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Oggetto *</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="subject-input"
              />
            </div>

            {/* Corpo (editabile WYSIWYG) */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">
                Testo email <span className="text-slate-400 font-normal">— formattazione rich text</span>
              </Label>
              <div className="bg-white rounded border border-slate-300 quill-wrapper" data-testid="body-wysiwyg">
                <ReactQuill
                  theme="snow"
                  value={body}
                  onChange={setBody}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'color': [] }, { 'background': [] }],
                      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                      [{ 'align': [] }],
                      ['link', 'blockquote'],
                      ['clean'],
                    ],
                  }}
                  formats={[
                    'header', 'bold', 'italic', 'underline', 'strike',
                    'color', 'background', 'list', 'bullet', 'align',
                    'link', 'blockquote',
                  ]}
                  style={{ minHeight: 240 }}
                />
              </div>
              <style>{`.quill-wrapper .ql-editor { min-height: 220px; font-size: 14px; }`}</style>
              <p className="text-xs text-slate-500 italic">
                Modifica il testo con la barra degli strumenti. Usa <strong>💾 Salva Template</strong> per salvare le modifiche sul CRM e riutilizzarle la prossima volta.
              </p>
            </div>

            {/* Allegati */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">
                Allegati <span className="text-slate-400 font-normal">— opzionale (max {MAX_TOTAL_MB} MB totali)</span>
              </Label>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={(e) => handleFilesSelected(e.target.files)}
                  className="hidden"
                  data-testid="attachments-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="add-attachment-btn"
                >
                  <Paperclip className="w-4 h-4 mr-2" />
                  Allega file
                </Button>
                <span className="text-xs text-slate-500">
                  {attachments.length === 0
                    ? "Nessun file selezionato"
                    : `${attachments.length} file · ${formatBytes(attachments.reduce((s, a) => s + a.size, 0))}`}
                </span>
              </div>
              {attachmentError && (
                <p className="text-xs text-red-600 font-medium" data-testid="attachment-error">
                  {attachmentError}
                </p>
              )}
              {attachments.length > 0 && (
                <div className="space-y-1 border border-slate-200 rounded-lg p-2 bg-slate-50">
                  {attachments.map((a, i) => (
                    <div
                      key={`${a.filename}-${i}`}
                      className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded px-2 py-1.5"
                      data-testid={`attachment-item-${i}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Paperclip className="w-4 h-4 text-slate-500 shrink-0" />
                        <span className="text-sm text-slate-800 truncate">{a.filename}</span>
                        <span className="text-xs text-slate-500 shrink-0">({formatBytes(a.size)})</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAttachment(i)}
                        data-testid={`remove-attachment-${i}`}
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpenDialog(false)}
              data-testid="cancel-draft-btn"
            >
              Annulla
            </Button>
            <Button
              type="button"
              onClick={handleSaveTemplate}
              disabled={savingTpl}
              variant="outline"
              className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              data-testid="save-template-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingTpl ? "Salvataggio..." : "Salva Template"}
            </Button>
            <Button
              type="button"
              onClick={handleCreateDraft}
              disabled={creating || !recipient || !recipient.email}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="create-draft-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {creating ? "Creazione..." : "Crea Bozza"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
